<template>
    <b-modal
        id="show-group-modal"
        v-model="display"
        :title="`${causer.full_name} ${$t('group_pair.request')} ${subject}`"
        :ok-title="$t('common.close')"
        ok-only
        size="lg"
        dialog-class="assignment-modal"
        modal-class="apprentice-default-modal"
        @ok="display = false">
        <div class="group-pair-log-details">
            <div class="heading status-heading">
                <div class="status">{{ action }}</div>
                <div
                    class="date"
                    v-if="created_at.length">
                    {{ created_at | date_time_om }}
                </div>
            </div>

            <div class="motivation">
                <div class="heading">
                    {{ $t('group_pair.motivation') }}
                </div>

                <div class="description">
                    {{ explanation }}
                </div>
            </div>

            <div
                class="courses"
                v-if="courses.length > 0">
                <div class="heading">{{ $t('group_pair.courses') }}</div>
                <b-badge
                    class="group-pair-course"
                    v-for="(course, index) of courses"
                    variant="primary"
                    :key="index">
                    {{ course }}
                </b-badge>
            </div>

            <ul class="students">
                <li class="heading">{{ $t('group_pair.courses') }}</li>
                <li
                    class="student item"
                    v-for="(student, index) of students"
                    :key="index">
                    <span class="name">{{ student.full_name }}</span>

                    <b-dropdown
                        class="group-pair-student-courses"
                        v-if="student.courses.length > 0"
                        split
                        right
                        :ref="`student-course-${index}`"
                        split-variant="outline-primary"
                        variant="primary"
                        :text="$t('group_pair.students')"
                        @click="showStudentCourses(index)">
                        <b-dropdown-text
                            v-if="student.courses.length === 0"
                            text-class="not-found">
                            {{ $t('group_pair.no_courses_chosen') }}
                        </b-dropdown-text>

                        <b-dropdown-text
                            v-for="(course, index) in student.courses"
                            v-else
                            :key="index">
                            {{ course }}
                        </b-dropdown-text>
                    </b-dropdown>
                </li>
            </ul>

            <ul class="group-pair-teachers">
                <li class="heading">{{ $t('group_pair.teachers') }}</li>

                <li
                    class="teacher-item not-found"
                    v-if="teachers.length === 0">
                    {{ $t('group_pair.no_teachers_chosen') }}
                </li>

                <li
                    class="teacher-item"
                    v-for="teacher in teachers"
                    v-else
                    :key="teacher.id">
                    <span class="name">{{ teacher }}</span>
                </li>
            </ul>

            <div class="feedback">
                <div class="heading">Feedback</div>

                <div class="description">
                    {{ feedback !== null ? feedback : this.$t('group_pair.no_feedback_given_yet') }}
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
export default {
    name: 'design-2021-show-group-pair-log-modal-component',
    data() {
        return {
            display: false,
            causer: '',
            action: '',
            courses: [],
            students: [],
            teachers: [],
            subject: '',
            explanation: '',
            feedback: '',
            created_at: '',
        };
    },
    methods: {
        show(log) {
            this.causer = log.causer;
            this.action = log.action;
            this.courses = log.courses;
            this.students = log.students;
            this.teachers = log.teachers;
            this.subject = log.subject;
            this.explanation = log.explanation;
            this.feedback = log.feedback;
            this.created_at = log.created_at;
            this.display = true;
        },

        showStudentCourses(index) {
            const refName = `student-course-${index}`;
            this.$refs[refName][0].$el.children[1].click();
        },
    },
};
</script>

<style scoped lang="scss">
.heading {
    font-weight: $font-weight-medium;
    font-size: 1.125rem;

    &.status-heading {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        justify-content: space-between;
        column-gap: $grid-gutter-width;
    }
}

ul {
    padding: 0;

    li {
        list-style: none;
    }
}

.dropdown ::v-deep {
    .btn {
        line-height: 1;
        padding: map-get($grid-gutter-spacing, 'five') map-get($grid-gutter-spacing, 'ten');
    }

    li {
        width: 250px;
    }

    .b-dropdown-text {
        color: $primary;
        font-weight: $font-weight-medium;
    }
}

.group-pair-log-details {
    > div:not(:last-child),
    > ul:not(:last-child) {
        margin-bottom: $grid-gutter-width;
    }
}

.status {
    color: $primary;
    font-weight: $font-weight-medium;
    flex-grow: 1;
}

.date {
    color: $text-muted-color;
    font-size: $default-font-size;
    text-align: right;
    flex-grow: 1;
}

.motivation,
.feedback {
    .heading {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }

    .description {
        border-radius: $border-radius-content;
        background-color: $border-color;
        color: $text-color;
        min-height: 100px;
        padding: map-get($grid-gutter-spacing, 'fifteen');
    }
}

.courses {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: map-get($grid-gutter-spacing, 'ten');
    row-gap: map-get($grid-gutter-spacing, 'fifteen');

    .heading {
        flex: 1 0 100%;
    }

    .badge {
        font-size: 0.75rem;
        font-weight: $font-weight-medium;
        padding: map-get($grid-gutter-spacing, 'four') map-get($grid-gutter-spacing, 'eight');
    }
}

.item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: map-get($grid-gutter-spacing, 'fifteen');
    padding: map-get($grid-gutter-spacing, 'eight');
    border-radius: 8px;

    .name {
        flex: 1 0 calc(100% - 125px);
    }

    .btn-remove,
    .btn-undo-remove {
        width: 23px;
        font-size: 1.125rem;
    }

    &:not(:last-child) {
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
    }

    &:nth-child(odd) {
        background-color: $secondary-background-color;
    }
}

.group-pair-student {
    .dropdown ::v-deep {
        .btn {
            line-height: 1;
            padding: map-get($grid-gutter-spacing, 'five') map-get($grid-gutter-spacing, 'ten');
            font-size: $default-font-size;
        }

        li {
            width: 250px;
        }

        .b-dropdown-text {
            color: $primary;
            font-weight: $font-weight-medium;
            font-size: $default-font-size;
        }
    }
}

.group-pair-teachers {
    .teacher-item {
        &.not-found {
            padding: map-get($grid-gutter-spacing, 'eight') 0;
            color: $text-muted-color;
            align-items: flex-start;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .status {
        flex-grow: 0;
        flex: 0 1 calc(100% - 145px);
    }

    .date {
        flex-grow: 0;
        flex: 0 1 115px;
    }
}
</style>
